import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase, supabaseReady } from "./supabase";
import { EVENT_COLUMNS, type EventRow, type EventType } from "./events";
import { cacheEvents, enqueue, getOutbox, readCachedEvents } from "./offline/db";
import { applyOutbox, flushOutbox, isNetworkError } from "./offline/sync";

// Optimistic + offline-tolerant event store. A mutation writes locally first;
// it then goes to Supabase, or — if the network is down — into an IndexedDB
// outbox that is replayed on reconnect. A feed logged at 2am is never lost.
// Optimistic rows carry a temp `id` (prefix `tmp-`) until the server assigns one.

export type NewEvent = {
  type: EventType;
  at?: string; // ISO; defaults to now
  end_at?: string | null;
  amount_oz?: number;
  duration_min?: number;
  side?: "L" | "R" | "both";
  sleep_kind?: "nap" | "night";
  diaper_kind?: "wet" | "dirty" | "both";
  cue_tier?: 1 | 2 | 3;
  milestone_key?: string;
  weight_grams?: number;
  note?: string;
  caretaker_label?: string;
  source?: "manual" | "nl-parsed" | "realtime";
};

type State = {
  events: EventRow[];
  loading: boolean;
  error: string | null;
};

const HISTORY_DAYS = 14;
const FLUSH_INTERVAL_MS = 30_000;

function tempId(): string {
  return `tmp-${crypto.randomUUID()}`;
}

function isTemp(id: string): boolean {
  return id.startsWith("tmp-");
}

function sortNewestFirst(rows: EventRow[]): EventRow[] {
  return [...rows].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

function dedupeById(rows: EventRow[]): EventRow[] {
  return rows.filter((e, i, arr) => arr.findIndex((x) => x.id === e.id) === i);
}

export function useEvents(babyId: string) {
  const [{ events, loading, error }, setState] = useState<State>({
    events: [],
    loading: true,
    error: null,
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [online, setOnline] = useState(() => navigator.onLine);
  const lastDeletedRef = useRef<EventRow | null>(null);

  const refreshPending = useCallback(async () => {
    setPendingCount((await getOutbox(babyId)).length);
  }, [babyId]);

  // Swap a just-synced optimistic row for the real server row.
  const applySync = useCallback((tmpId: string, realRow: EventRow) => {
    setState((s) => ({
      ...s,
      events: sortNewestFirst(dedupeById(s.events.map((e) => (e.id === tmpId ? realRow : e)))),
    }));
  }, []);

  // Initial load: show the cached day instantly, flush anything queued, then
  // overlay fresh server truth. Offline, the cached state simply stands.
  useEffect(() => {
    if (!babyId || !supabaseReady) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    let cancelled = false;

    (async () => {
      const cached = await readCachedEvents(babyId);
      if (cancelled) return;
      if (cached) setState({ events: cached, loading: false, error: null });

      await flushOutbox(babyId, applySync);
      if (cancelled) return;

      const since = new Date(Date.now() - HISTORY_DAYS * 86_400_000).toISOString();
      const { data, error: fetchError } = await supabase
        .from("events")
        .select(EVENT_COLUMNS)
        .eq("baby_id", babyId)
        .gte("at", since)
        .order("at", { ascending: false });
      if (cancelled) return;

      if (fetchError) {
        // Offline / failed — keep whatever we already have (cache or empty).
        setState((s) => ({ ...s, loading: false, error: cached ? null : fetchError.message }));
      } else {
        const pending = await getOutbox(babyId);
        if (cancelled) return;
        setState({
          events: applyOutbox((data ?? []) as EventRow[], pending),
          loading: false,
          error: null,
        });
      }
      await refreshPending();
    })();

    return () => {
      cancelled = true;
    };
  }, [babyId, applySync, refreshPending]);

  // Realtime subscription — other caretakers' changes land here.
  useEffect(() => {
    if (!babyId || !supabaseReady) return;

    const channel = supabase
      .channel(`events:${babyId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "events", filter: `baby_id=eq.${babyId}` },
        (payload) => {
          const row = payload.new as EventRow;
          setState((s) => {
            // Replace any optimistic row with matching at+type, else prepend.
            const optimisticIdx = s.events.findIndex(
              (e) =>
                isTemp(e.id) &&
                e.type === row.type &&
                Math.abs(new Date(e.at).getTime() - new Date(row.at).getTime()) < 5_000,
            );
            if (optimisticIdx >= 0) {
              const next = [...s.events];
              next[optimisticIdx] = row;
              return { ...s, events: sortNewestFirst(next) };
            }
            if (s.events.some((e) => e.id === row.id)) return s;
            return { ...s, events: sortNewestFirst([row, ...s.events]) };
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "events", filter: `baby_id=eq.${babyId}` },
        (payload) => {
          const row = payload.new as EventRow;
          setState((s) => ({
            ...s,
            events: sortNewestFirst(s.events.map((e) => (e.id === row.id ? row : e))),
          }));
        },
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "events", filter: `baby_id=eq.${babyId}` },
        (payload) => {
          const oldRow = payload.old as EventRow;
          setState((s) => ({ ...s, events: s.events.filter((e) => e.id !== oldRow.id) }));
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [babyId]);

  // Persist the event list so the app opens to the last-known day offline.
  useEffect(() => {
    if (!babyId || loading) return;
    cacheEvents(babyId, events).catch(() => {});
  }, [babyId, events, loading]);

  // Track connectivity and drain the outbox on reconnect / refocus / a slow tick.
  useEffect(() => {
    if (!babyId) return;
    const drain = () => {
      flushOutbox(babyId, applySync).then(refreshPending);
    };
    const goOnline = () => {
      setOnline(true);
      drain();
    };
    const goOffline = () => setOnline(false);
    const onVisible = () => {
      if (document.visibilityState === "visible") drain();
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    document.addEventListener("visibilitychange", onVisible);
    const iv = setInterval(drain, FLUSH_INTERVAL_MS);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(iv);
    };
  }, [babyId, applySync, refreshPending]);

  const insert = useCallback(
    async (input: NewEvent): Promise<EventRow> => {
      // Stamp who logged this — read from the local session (no network).
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id ?? null;
      const optimistic: EventRow = {
        id: tempId(),
        baby_id: babyId,
        type: input.type,
        at: input.at ?? new Date().toISOString(),
        end_at: input.end_at ?? null,
        amount_oz: input.amount_oz ?? null,
        duration_min: input.duration_min ?? null,
        side: input.side ?? null,
        sleep_kind: input.sleep_kind ?? null,
        diaper_kind: input.diaper_kind ?? null,
        cue_tier: input.cue_tier ?? null,
        milestone_key: input.milestone_key ?? null,
        weight_grams: input.weight_grams ?? null,
        note: input.note ?? null,
        caretaker_label: input.caretaker_label ?? null,
        user_id: userId,
        source: input.source ?? "manual",
        created_at: new Date().toISOString(),
      };

      setState((s) => ({ ...s, events: sortNewestFirst([optimistic, ...s.events]) }));

      const queueIt = async () => {
        await enqueue({ kind: "insert", babyId, localId: optimistic.id, row: optimistic });
        await refreshPending();
      };

      if (!navigator.onLine) {
        await queueIt();
        return optimistic;
      }

      let result;
      try {
        result = await supabase
          .from("events")
          .insert({
            baby_id: babyId,
            type: optimistic.type,
            at: optimistic.at,
            end_at: optimistic.end_at,
            amount_oz: optimistic.amount_oz,
            duration_min: optimistic.duration_min,
            side: optimistic.side,
            sleep_kind: optimistic.sleep_kind,
            diaper_kind: optimistic.diaper_kind,
            cue_tier: optimistic.cue_tier,
            milestone_key: optimistic.milestone_key,
            weight_grams: optimistic.weight_grams,
            note: optimistic.note,
            caretaker_label: optimistic.caretaker_label,
            user_id: optimistic.user_id,
            source: optimistic.source,
          })
          .select(EVENT_COLUMNS)
          .single();
      } catch {
        // fetch threw — network down. Queue it and keep the optimistic row.
        await queueIt();
        return optimistic;
      }

      const { data, error: insertError } = result;
      if (insertError || !data) {
        if (isNetworkError(insertError)) {
          await queueIt();
          return optimistic;
        }
        // Hard server rejection — roll the optimistic row back.
        setState((s) => ({
          ...s,
          events: s.events.filter((e) => e.id !== optimistic.id),
          error: insertError?.message ?? "Failed to save",
        }));
        throw insertError ?? new Error("Failed to save");
      }

      const row = data as EventRow;
      setState((s) => ({
        ...s,
        events: sortNewestFirst(dedupeById(s.events.map((e) => (e.id === optimistic.id ? row : e)))),
        error: null,
      }));
      return row;
    },
    [babyId, refreshPending],
  );

  const update = useCallback(
    async (id: string, patch: Partial<EventRow>) => {
      setState((s) => ({
        ...s,
        events: sortNewestFirst(s.events.map((e) => (e.id === id ? { ...e, ...patch } : e))),
      }));

      const queueIt = async () => {
        await enqueue({ kind: "update", babyId, targetId: id, patch });
        await refreshPending();
      };

      // A temp row isn't on the server yet — queue so the flush can remap its id.
      if (isTemp(id) || !navigator.onLine) {
        await queueIt();
        return;
      }

      let result;
      try {
        result = await supabase.from("events").update(patch).eq("id", id);
      } catch {
        await queueIt();
        return;
      }
      if (result.error) {
        if (isNetworkError(result.error)) await queueIt();
        else setState((s) => ({ ...s, error: result.error!.message }));
      }
    },
    [babyId, refreshPending],
  );

  const remove = useCallback(
    async (id: string): Promise<EventRow | null> => {
      let removed: EventRow | null = null;
      setState((s) => {
        removed = s.events.find((e) => e.id === id) ?? null;
        return { ...s, events: s.events.filter((e) => e.id !== id) };
      });
      lastDeletedRef.current = removed;

      const queueIt = async () => {
        await enqueue({ kind: "remove", babyId, targetId: id });
        await refreshPending();
      };

      if (isTemp(id) || !navigator.onLine) {
        await queueIt();
        return removed;
      }

      let result;
      try {
        result = await supabase.from("events").delete().eq("id", id);
      } catch {
        await queueIt();
        return removed;
      }
      if (result.error) {
        if (isNetworkError(result.error)) await queueIt();
        else setState((s) => ({ ...s, error: result.error!.message }));
      }
      return removed;
    },
    [babyId, refreshPending],
  );

  const value = useMemo(
    () => ({ events, loading, error, insert, update, remove, pendingCount, online }),
    [events, loading, error, insert, update, remove, pendingCount, online],
  );
  return value;
}

export type EventsApi = ReturnType<typeof useEvents>;
